import React from 'react';
import { useTranslation } from 'react-i18next';

import { EvaluationSheetResultFieldsFragment, EvaluationSheetResultItemFieldsFragment } from '../../../../../../../../../../../generated/core-api/graphql-react-query';
import useRatioFormat from '../../../../../../../../../../../Hooks/useRatioFormat';
import useModuleGroupedEvaluationSheetResultItems from './useModuleGroupedEvaluationSheetResultItems';

function getItemLabel(item: EvaluationSheetResultItemFieldsFragment) {
  if (item.__typename === 'EvaluationSheetResultAdvancedAnalysisItem') {
    return item.advancedAnalysisItem.name;
  }
  if (item.__typename === 'EvaluationSheetResultTopicItem') {
    return item.topic.text;
  }
  if (item.__typename === 'EvaluationSheetResultComplianceOperationItem') {
    return item.complianceOperationItem.title;
  }
  if (item.__typename === 'EvaluationSheetResultFactCheckModuleItem') {
    return item.factCheckModule.name;
  }
  throw new Error(`Unsupported item type: ${(item as any)?.__typename}`);
}

export default function useEvaluationSheetCsvRows(sheet: EvaluationSheetResultFieldsFragment | null | undefined) {
  const { groupedByModule } = useModuleGroupedEvaluationSheetResultItems(sheet?.items ?? []);
  const { t } = useTranslation('session');
  const totalFormat = useRatioFormat();
  const percentTotalFormat = useRatioFormat('percentAndRatio');

  const rows = React.useMemo(() => {
    if (!sheet) return [];

    return groupedByModule.flatMap(({
      module, items, totalDenom, totalNum,
    }) => {
      const moduleName = module?.name ?? '-';
      return items.map((item) => ({
        [t('details.evaluation-sheets.columns.module')]: moduleName,
        [t('details.evaluation-sheets.columns.module')]: getItemLabel(item),
        [t('details.evaluation-sheets.columns.score')]: totalFormat.format({ numerator: item.num ?? 0, denominator: item.denom ?? 0 }),
        [t('details.evaluation-sheets.columns.totalModule')]: percentTotalFormat.format({ numerator: totalNum, denominator: totalDenom }),
        [t('details.evaluation-sheets.columns.totalGlobal')]: percentTotalFormat.format({ numerator: sheet.num ?? 0, denominator: sheet.denom ?? 0 }),
        ...(item.__typename === 'EvaluationSheetResultAdvancedAnalysisItem' ? ({
          [t('details.evaluation-sheets.columns.verbatim')]: item.verbatim,
          [t('details.evaluation-sheets.columns.explanation')]: item.explanation,
        }) : ({
          [t('details.evaluation-sheets.columns.verbatim')]: '',
          [t('details.evaluation-sheets.columns.explanation')]: '',
        })),
      }));
    });
  }, [groupedByModule, percentTotalFormat, sheet, t, totalFormat]);

  return { rows };
}
